import React from "react";
import "../../../assets/css/buttons/legacyButton.css";

export interface ILegacyButton {
  text?: string;
  icon?: JSX.Element;
  style?: "filled" | "ghost" | "outlined";
  color?: "primary" | "accent" | "error" | "neutral" | "delete" | "excel";
  disabled?: boolean;
  isLoading?: boolean;
  size?:
    | "small"
    | "medium"
    | "large"
    | "medium-no-icon"
    | "dropdown"
    | "filter";
  content?: "text" | "textIcon" | "icon" | "iconSquare";
  /**
   * Only when `content="textIcon"`. `between` = label and icon at ends (default).
   * `cluster` = centered row with gap (e.g. member profile title bar).
   */
  textIconLayout?: "between" | "cluster";
  iconOrder?: "textFirst" | "iconFirst";
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  className?: string;
  link?: boolean;
  hoverColor?: string;
  tooltip?: string;
}

const LegacyButtonSpinner = (): JSX.Element => (
  <span
    aria-hidden="true"
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent shrink-0"
  />
);

const LegacyButton: React.FC<ILegacyButton> = ({
  size = "small",
  style = "filled",
  color = "primary",
  disabled = false,
  isLoading = false,
  link = false,
  content = "text",
  textIconLayout = "between",
  iconOrder = "textFirst",
  text,
  icon,
  className = "",
  hoverColor: ghostHoverColor = "#BC3423",
  tooltip,
  ...props
}: ILegacyButton): JSX.Element => {
  const customHoverStyle = style === "ghost" && ghostHoverColor ? {
    '--ghost-hover-color': ghostHoverColor,
  } as React.CSSProperties : undefined;
  const isDisabled = disabled || isLoading;
  const resolvedIcon = isLoading ? <LegacyButtonSpinner /> : icon;

  const textSizeMatch = className.match(/(!?text-\[[\d.]+px\]|!?text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl))/);
  const textSizeClass = textSizeMatch ? textSizeMatch[0] : "";

  const buttonContent = (
    <div
      onClick={!isDisabled ? props.onClick : () => {}}
      aria-busy={isLoading}
      className={`legacy-btn-${content} legacy-btn-${size} legacy-btn-${color} legacy-btn-${style} ${isDisabled ? "legacy-btn-disabled text-light-gray" : "cursor-pointer"} ${link && "legacy-btn-anchorbutton"} ${style === "ghost" && ghostHoverColor ? "legacy-btn-ghost-custom-hover" : ""} items-center uppercase font-decimal fill-current ${className}`}
      style={customHoverStyle}
    >
      {content === "text" && text && (
        <span className="inline-flex items-center justify-center gap-2 text-center">
          {isLoading && <LegacyButtonSpinner />}
          <span>{text}</span>
        </span>
      )}
      {(content === "icon" || content === "iconSquare") && (
        <span>{resolvedIcon}</span>
      )}
      {content === "textIcon" && (
        <div
          className={
            textIconLayout === "cluster"
              ? "flex flex-row items-center w-full justify-center gap-3 px-2 md:gap-4"
              : "flex flex-row items-center w-full !px-0 justify-between"
          }
        >
          {iconOrder === "textFirst" ? (
            <>
              {text && (
                <span className={`text-center justify-center items-center ${textSizeClass || "text-xs md:text-sm lg:text-base"}`}>
                  {text.toUpperCase()}
                </span>
              )}
              {resolvedIcon && <span>{resolvedIcon}</span>}
            </>
          ) : (
            <>
              {resolvedIcon && <span>{resolvedIcon}</span>}
              {text && (
                <span className={`text-center justify-center items-center ${textSizeClass || ""}`}>
                  {text.toUpperCase()}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  if (tooltip) {
    return (
      <div className="relative group inline-block h-full">
        {buttonContent}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none">
          <div className="bg-gray-font text-neutral text-sm md:text-base px-3 py-2 rounded shadow-lg whitespace-nowrap">
            {tooltip}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-font"></div>
          </div>
        </div>
      </div>
    );
  }

  return buttonContent;
};

export default LegacyButton;
